import * as React from "react";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TablePagination from "@mui/material/TablePagination";
import { useTheme } from "./ThemeContext";
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  CircularProgress,
} from "@mui/material";
import Chip from "@mui/material/Chip";
import { DeviceContext } from "../context/DeviceContext";
import { useContext, useEffect, useState } from "react";
import axios from "axios";

// Helper to format position type for display
const formatPositionType = (value) => {
  if (!value) return "Unknown";
  // Handle both dash-separated and underscore formats
  const normalized = value.replace(/_/g, "-").toLowerCase();
  return normalized
    .split("-")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

// Calculate P/L percentage
const calculatePL = (position) => {
  if (!position.entryPrice) return null;
  if (position.status === "CLOSED" && position.exitPrice) {
    const pl =
      ((position.exitPrice - position.entryPrice) / position.entryPrice) * 100;
    // For short positions, invert the P/L
    const posType = (position.type || position.positionType || "").toLowerCase();
    if (posType.includes("short") || posType.includes("sell")) {
      return -pl;
    }
    return pl;
  }
  return null;
};

export default function PositionsTable({ userId }) {
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(5);
  const { theme } = useTheme();
  const { isMobile } = useContext(DeviceContext);
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPositions = async () => {
      if (!userId) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const response = await axios.get(
          `${process.env.REACT_APP_SC_API_BASE_URL}/api/positions/user/${userId}`
        );
        setPositions(response.data || []);
        setError(null);
      } catch (err) {
        console.error("Error fetching positions:", err);
        setError("Failed to load positions");
        setPositions([]);
      } finally {
        setLoading(false);
      }
    };

    fetchPositions();
  }, [userId]);

  // Helper to get position type - handles both frontend and backend field names
  const getPositionType = (row) => row.type || row.positionType || "";

  const columns = [
    {
      id: "positionType",
      label: "Type",
      minWidth: "5rem",
      format: (value, row) => formatPositionType(getPositionType(row)),
      formatColor: (value, row) => {
        const posType = getPositionType(row).toLowerCase();
        if (
          posType.includes("buy") ||
          posType.includes("call") ||
          posType === "long"
        ) {
          return theme.palette.success.main;
        } else if (
          posType.includes("sell") ||
          posType.includes("put") ||
          posType.includes("short")
        ) {
          return theme.palette.error.main;
        } else {
          return theme.palette.warning.main;
        }
      },
    },
    {
      id: "ticker",
      label: "Ticker",
      minWidth: "4rem",
      formatColor: () => theme.palette.text.green,
    },
    {
      id: "status",
      label: "Status",
      minWidth: "4rem",
      align: "center",
      formatColor: (value) => {
        if (value === "OPEN") return theme.palette.info.main;
        if (value === "CLOSED") return theme.palette.text.disabled;
        if (value === "PARTIAL") return theme.palette.warning.main;
        return theme.palette.text.primary;
      },
    },
    {
      id: "entryPrice",
      label: "Entry",
      minWidth: "4rem",
      align: "right",
      format: (value) => (value ? "$" + parseFloat(value).toFixed(2) : "-"),
      formatColor: () => theme.palette.text.primary,
    },
    {
      id: "exitPrice",
      label: "Exit",
      minWidth: "4rem",
      align: "right",
      format: (value) => (value ? "$" + parseFloat(value).toFixed(2) : "-"),
      formatColor: () => theme.palette.text.primary,
    },
    {
      id: "quantity",
      label: "Qty",
      minWidth: "3rem",
      align: "right",
      format: (value) => (value ? parseFloat(value).toLocaleString() : "-"),
      formatColor: () => theme.palette.text.primary,
    },
    {
      id: "entryDate",
      label: "Date",
      minWidth: "5rem",
      align: "center",
      format: (value) =>
        value
          ? new Date(value).toLocaleDateString("en-US", {
              month: "short",
              day: "numeric",
              year: "2-digit",
            })
          : "-",
      formatColor: () => theme.palette.text.secondary,
    },
    {
      id: "pl",
      label: "P/L",
      minWidth: "4rem",
      align: "right",
      format: (value, row) => {
        const pl = calculatePL(row);
        if (pl === null) return "-";
        const sign = pl >= 0 ? "+" : "";
        return `${sign}${pl.toFixed(2)}%`;
      },
      formatColor: (value, row) => {
        const pl = calculatePL(row);
        if (pl === null) return theme.palette.text.secondary;
        return pl >= 0 ? theme.palette.success.main : theme.palette.error.main;
      },
    },
  ];

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  // Sort positions by entry date descending (newest first)
  const sortedPositions = [...positions].sort((a, b) => {
    const dateA = a.entryDate ? new Date(a.entryDate) : new Date(0);
    const dateB = b.entryDate ? new Date(b.entryDate) : new Date(0);
    return dateB - dateA;
  });

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
        <CircularProgress sx={{ color: theme.palette.primary.main }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Paper
        sx={{
          width: "100%",
          p: 3,
          backgroundColor: theme.palette.background.paper,
          borderRadius: "1rem",
        }}
      >
        <Typography variant="body2" color="error" align="center">
          {error}
        </Typography>
      </Paper>
    );
  }

  if (!positions || positions.length === 0) {
    return (
      <Paper
        sx={{
          width: "100%",
          p: 3,
          backgroundColor: theme.palette.background.paper,
          borderRadius: "1rem",
        }}
      >
        <Typography variant="body2" color="textSecondary" align="center">
          No positions found.
        </Typography>
      </Paper>
    );
  }

  function renderPositionTableDesktop() {
    const paginatedData = sortedPositions.slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage
    );

    return (
      <>
        <TableContainer sx={{ maxHeight: 440 }}>
          <Table stickyHeader aria-label="positions table" size="small">
            <TableHead>
              <TableRow>
                {columns.map((column) => (
                  <TableCell
                    key={column.id}
                    align={column.align}
                    sx={{
                      minWidth: column.minWidth,
                      backgroundColor: theme.palette.background.gray,
                      fontWeight: "bold",
                      color: theme.palette.text.green,
                      padding: "0.6rem",
                    }}
                  >
                    {column.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedData.map((row) => {
                return (
                  <TableRow
                    hover
                    tabIndex={-1}
                    key={row.positionId || row.id}
                  >
                    {columns.map((column) => {
                      const value = row[column.id];
                      return (
                        <TableCell
                          key={column.id}
                          align={column.align}
                          style={{
                            color: column.formatColor
                              ? column.formatColor(value, row)
                              : theme.palette.text.primary,
                            padding: "0.6rem",
                          }}
                        >
                          {column.format ? column.format(value, row) : value}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={sortedPositions.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{
            color: theme.palette.text.green,
            backgroundColor: theme.palette.background.gray,
          }}
        />
      </>
    );
  }

  // Mobile Card View Renderer
  function renderPositionsTableMobile() {
    return sortedPositions.map((row, index) => {
      const posType = getPositionType(row);
      const formattedType = formatPositionType(posType);
      const posTypeLower = posType.toLowerCase();
      const isBullish =
        posTypeLower.includes("buy") ||
        posTypeLower.includes("call") ||
        posTypeLower === "long";

      const typeColor = isBullish
        ? theme.palette.success.main
        : theme.palette.error.main;

      const statusColor =
        row.status === "OPEN"
          ? theme.palette.info.main
          : row.status === "CLOSED"
          ? theme.palette.text.disabled
          : theme.palette.warning.main;

      const pl = calculatePL(row);

      return (
        <Box
          key={row.positionId || index}
          sx={{
            width: "100%",
            overflow: "hidden",
            backgroundColor: theme.palette.background.default,
          }}
        >
          <Card
            sx={{
              marginBottom: "0.5rem",
              backgroundColor: theme.palette.background.paper,
              color: theme.palette.text.primary,
            }}
          >
            <CardContent sx={{ padding: "0.75rem" }}>
              <Grid
                container
                spacing={1}
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <Grid item xs={4}>
                  <Typography
                    variant="body1"
                    sx={{
                      fontWeight: "bold",
                      color: theme.palette.text.green,
                      marginBottom: "0.25rem",
                    }}
                  >
                    {row.ticker}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: typeColor,
                      fontWeight: "medium",
                    }}
                  >
                    {formattedType}
                  </Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography
                    variant="body2"
                    sx={{
                      color: theme.palette.text.secondary,
                      marginBottom: "0.25rem",
                    }}
                  >
                    Entry: {row.entryPrice ? `$${parseFloat(row.entryPrice).toFixed(2)}` : "-"}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: theme.palette.text.secondary,
                    }}
                  >
                    Qty: {row.quantity ? parseFloat(row.quantity).toLocaleString() : "-"}
                  </Typography>
                </Grid>
                <Grid item xs={4} sx={{ textAlign: "right" }}>
                  {pl !== null ? (
                    <Chip
                      size="small"
                      sx={{
                        backgroundColor:
                          pl >= 0
                            ? theme.palette.success.main
                            : theme.palette.error.main,
                        color: "#fff",
                        fontWeight: "bold",
                      }}
                      label={`${pl >= 0 ? "+" : ""}${pl.toFixed(2)}%`}
                    />
                  ) : (
                    <Chip
                      size="small"
                      sx={{
                        backgroundColor: statusColor,
                        color: "#fff",
                        fontWeight: "bold",
                      }}
                      label={row.status}
                    />
                  )}
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Box>
      );
    });
  }

  return (
    <Paper
      sx={{
        width: "100%",
        overflow: "hidden",
        backgroundColor: theme.palette.background.paper,
        borderRadius: "1rem",
        borderColor: theme.palette.primary.main,
        borderWidth: "1px",
      }}>
      {isMobile ? renderPositionsTableMobile() : renderPositionTableDesktop()}
    </Paper>
  );
}
